import React, {useState, useEffect} from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {CardContent, Grid, Stack} from '@mui/material';

import {Breadcrumbs, Button, Chip} from '../../../../components';
import {
  DataDetail,
  typeRoutes,
} from '../../../../types/typeReferralTransactions';
import {path} from '../../../../routes/path';
import {formatDate} from '../../../../utils';
import queryString from 'query-string';
import ReferralTransactionService from '../../../../services/ReferralProgramme/referral-transaction.service';
import {
  POINT_SUCCESS_COLOR,
  REFERRAL_TRANSACTION_STATUS,
  REFERRAL_TRANSACTION_STATUS_COLOR,
  REFERRAL_TRANSACTION_STATUS_LABEL,
} from '../../../../constants/status';
import {ColorType} from '../../../../types/typeChip';

// styles
import {
  Typography,
  StyledDivBreadcrumbs,
  Card,
  Divider,
  StyledCardContent,
  StyledChip,
} from './ReferralTransactionDetail.style';
import {IsNullOrEmptyOrUndefined} from '../../../../utils/helpers';
import useAuth from '../../../../hooks/useAuth';
import {rbacPermissions} from '../../../../constants/roleBasedAccessControlConstants';

const COMPLETE = '1';
const PENDING = '2';
const DECLINED = '3';

const processId_eq_LIST = {
  [COMPLETE]: 'Approved',
  [PENDING]: 'Pending',
  [DECLINED]: 'Declined',
};

function ReferralTransactionDetail() {
  const {checkRbac} = useAuth();
  const navigate = useNavigate();
  const paramsUrl = useParams();
  const locationUrl = useLocation();
  const idDetail = paramsUrl?.id;
  const membershipId = locationUrl?.state?.membershipId;
  const merchantId = locationUrl?.state?.merchantId;

  const formatDateTime = 'DD/MM/YYYY HH:mm';
  // states
  const [dataDetail, setDataDetail] = useState<DataDetail>({});

  const renderCaption = (value: string) => {
    if (value) {
      return (
        <Typography color="rgba(0, 0, 0, 0.5)" mb="31px" variant="h6">
          {value}:
        </Typography>
      );
    }
    return null;
  };

  const renderSubtitle = value => {
    return (
      <Typography mb="31px" variant="h6">
        {value ? value : '--'}
      </Typography>
    );
  };

  const getDataDetail = () => {
    new ReferralTransactionService().getOne(idDetail).then(res => {
      if (res && res.data && res.data.data) {
        setDataDetail(res.data.data);
      }
    });
  };

  const renderLink = () => {
    return `/${path.REFERRAL_PROGRAMME}/${
      path.REFERRAL_TRANSACTIONS
    }?${queryString.stringify(locationUrl?.state?.filterParams)}`;
  };

  const renderStatus = (value: any) => {
    let label = '--';
    let color = 'default';
    switch (value.processId) {
      case 1:
        label = processId_eq_LIST[value.processId];
        color = POINT_SUCCESS_COLOR[value.processId];
        break;
      case 2:
        label = processId_eq_LIST[value.processId];
        color = POINT_SUCCESS_COLOR[value.processId];
        break;
      case 3:
        label = processId_eq_LIST[value.processId];
        color = POINT_SUCCESS_COLOR[value.processId];
        break;
      default:
        break;
    }
    return <Chip label={label} color={color as ColorType} />;
  };

  useEffect(() => {
    getDataDetail();
  }, []);

  return (
    <React.Fragment>
      <Helmet title="Referral Transaction Detail" />

      <Typography variant="h3" gutterBottom>
        Referral Transaction Detail
      </Typography>
      <StyledDivBreadcrumbs>
        <Breadcrumbs
          routes={[
            typeRoutes('Referral Programme', undefined),
            typeRoutes('Referral Transactions', renderLink(), false, {
              membershipId: membershipId,
              merchantId: merchantId,
            }),
            typeRoutes('Detail', undefined, true),
          ]}
        />
      </StyledDivBreadcrumbs>
      <Divider my={6} />
      <Grid container mb={9}>
        <Grid item xs={12} md={12}>
          <StyledChip>{renderStatus(dataDetail)}</StyledChip>
          <Card
            pl={12}
            pr={12}
            sx={{
              position: 'relative',
              bottom: '16px',
            }}
          >
            <StyledCardContent>
              <Divider my={6} />
              <Grid wrap="nowrap" container justifyContent="center">
                <Grid item xs={4} md={4}>
                  {renderCaption('Referral type')}
                  {renderCaption('Referral Partner’s name')}
                  {renderCaption('Referrer reference')}
                  {renderCaption('Referral transaction date and time')}
                  {renderCaption('Referee’s Account ID')}
                  {renderCaption('Referral code used')}
                  {renderCaption('Points rewarded')}
                  {renderCaption('Transaction no.')}
                </Grid>
                <Grid item xs={8} md={8}>
                  {renderSubtitle(
                    dataDetail?.referralType === 1
                      ? 'Partnership'
                      : 'Islander Member'
                  )}
                  {renderSubtitle(dataDetail?.referralMerchantName)}
                  {renderSubtitle(dataDetail?.referralCampaignId)}
                  {renderSubtitle(
                    formatDate(dataDetail?.createdDateTime, formatDateTime)
                  )}
                  {renderSubtitle(dataDetail?.accountId)}
                  {renderSubtitle(dataDetail?.referralCode)}
                  {renderSubtitle(dataDetail?.pointsRewarded)}
                  {renderSubtitle(dataDetail?.transactionId)}
                </Grid>
              </Grid>
            </StyledCardContent>
          </Card>
        </Grid>
        <Divider my={6} />
      </Grid>
      <Grid container>
        <Grid item xs={12} lg={12}>
          <Card px={6} pt={3}>
            <CardContent>
              <Grid container direction="row" justifyContent="center">
                <Stack direction="row" spacing={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="button"
                    onClick={() => {
                      navigate(-1);
                    }}
                    // width="125px"
                  >
                    Go to Transactions
                  </Button>
                  {checkRbac(
                    {
                      permissionString:
                        rbacPermissions.ISLANDER_MEMBERS__LISTING__EDIT,
                    },
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      onClick={() => {
                        navigate(
                          `/${path.ISLAND_MEMBERS}/${dataDetail?.referrerMembershipId}`
                        );
                      }}
                      disabled={IsNullOrEmptyOrUndefined(
                        dataDetail?.referrerMembershipId
                      )}
                    >
                      Go to Referrer
                    </Button>
                  )}
                  {checkRbac(
                    {
                      permissionString:
                        rbacPermissions.ISLANDER_MEMBERS__LISTING__EDIT,
                    },
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      onClick={() => {
                        navigate(
                          `/${path.ISLAND_MEMBERS}/${dataDetail?.membershipId}`
                        );
                      }}
                    >
                      Go to Referee
                    </Button>
                  )}
                </Stack>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default ReferralTransactionDetail;
